import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItemButton,
    Radio,
    Typography,
} from "@mui/material";
import {
    AddProductsResult,
    CampaignSummary,
    campaignService,
} from "../../services/campaignService";
import { STATUS_CHIP, discountText, whenText } from "../../pages/campaigns/CampaignList";

interface Props {
    open: boolean;
    productIds: string[];
    onClose: () => void;
    /** After products were added (refresh the list: prices may have changed). */
    onAdded: () => void;
}

/** Products page → bulk "Add to campaign": pick a live or scheduled campaign;
 * the products join at the end, priced with its discount. */
export const AddToCampaignDialog: React.FC<Props> = ({ open, productIds, onClose, onAdded }) => {
    const navigate = useNavigate();
    const [campaigns, setCampaigns] = useState<CampaignSummary[] | null>(null);
    const [chosen, setChosen] = useState<string>("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState<AddProductsResult | null>(null);

    useEffect(() => {
        if (!open) return;
        setResult(null);
        setError("");
        setChosen("");
        setCampaigns(null);
        campaignService
            .list()
            .then((all) => setCampaigns(all.filter((c) => c.status !== "ended")))
            .catch(() => {
                setCampaigns([]);
                setError("Couldn't load campaigns.");
            });
    }, [open]);

    const add = async () => {
        setBusy(true);
        setError("");
        try {
            const res = await campaignService.addProducts(chosen, productIds);
            setResult(res);
            if (res.added > 0) onAdded();
        } catch (err: any) {
            setError(err?.response?.data?.detail || "Couldn't add them. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const chosenCampaign = campaigns?.find((c) => c.campaign_id === chosen);
    const n = productIds.length;

    return (
        <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="sm">
            <DialogTitle>
                {result ? "Added to campaign" : `Add ${n} product${n === 1 ? "" : "s"} to a campaign`}
            </DialogTitle>
            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {result ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Alert severity={result.added ? "success" : "info"}>
                            {result.added
                                ? `${result.added} product${result.added === 1 ? "" : "s"} added to ${chosenCampaign?.name}.`
                                : "Nothing new was added."}
                        </Alert>
                        {result.without_discount.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2">Added without a campaign price</Typography>
                                {result.without_discount.map((s) => (
                                    <Typography key={s.product_id} variant="body2" color="text.secondary">
                                        {s.name} — {s.reason.toLowerCase()}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                        {result.skipped.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2">Not added</Typography>
                                {result.skipped.map((s) => (
                                    <Typography key={s.product_id} variant="body2" color="text.secondary">
                                        {s.name} — {s.reason.toLowerCase()}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </Box>
                ) : campaigns === null ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : campaigns.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            There's no live or upcoming campaign yet.
                        </Typography>
                        <Button variant="outlined" onClick={() => navigate("/campaigns/new")}>
                            Create a campaign
                        </Button>
                    </Box>
                ) : (
                    <List disablePadding>
                        {campaigns.map((c) => (
                            <ListItemButton
                                key={c.campaign_id}
                                selected={chosen === c.campaign_id}
                                onClick={() => setChosen(c.campaign_id)}
                                sx={{ borderRadius: 1.5, gap: 1, alignItems: "flex-start" }}
                            >
                                <Radio checked={chosen === c.campaign_id} sx={{ p: 0.5, mt: -0.25 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography fontWeight={600} noWrap>
                                            {c.name}
                                        </Typography>
                                        <Chip size="small" label={STATUS_CHIP[c.status].label} color={STATUS_CHIP[c.status].color} />
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        {discountText(c)} · {c.product_count} products · {whenText(c)}
                                    </Typography>
                                </Box>
                            </ListItemButton>
                        ))}
                    </List>
                )}
                {!result && chosenCampaign && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        {chosenCampaign.discount_type !== "none"
                            ? `They start at ${discountText(chosenCampaign).replace(" by default", "")} while it's live — you can change any price in the campaign. Products already priced by another running campaign are skipped.`
                            : "They join without a campaign price — set their prices in the campaign. They get its badge either way."}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                {result ? (
                    <>
                        <Button onClick={() => navigate(`/campaigns/${chosen}`)}>Open campaign</Button>
                        <Button variant="contained" onClick={onClose}>
                            Done
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={onClose} disabled={busy}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={add}
                            disabled={!chosen || busy}
                            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
                        >
                            Add {n} product{n === 1 ? "" : "s"}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AddToCampaignDialog;
